/** The guest app's frame around a step — bookingmgmt `components/Layout.js` (app bar),
 *  `Sidebar.js` (trip card, the steps with their status icons, "Step N of M") and
 *  `LanguageSelector.js` — for the desktop (`wide`) and the phone layout. The app bar's
 *  language button and Sign out are drawn, not working (they are the guest's session). */
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import RadioButtonUncheckedOutlinedIcon from '@mui/icons-material/RadioButtonUncheckedOutlined';
import { Box, Button, ButtonBase, Chip, Divider, IconButton, LinearProgress, Menu, MenuItem, Typography } from '@mui/material';
import { useState, type ReactNode } from 'react';

import { GUEST } from '../guestTokens';
import type { GuestPreviewSample, PreviewStepDef } from '../previewModel';

// ---------------------------------------------------------------------------
// Languages
// ---------------------------------------------------------------------------

/** `utils/helpers.js getFlagEmoji` (English shows the British flag). */
export function flagEmoji(code: string): string {
  const cc = code === 'en' ? 'gb' : code;
  if (cc.length !== 2) return '';
  return String.fromCodePoint(...cc.toUpperCase().split('').map((c) => 127397 + c.charCodeAt(0)));
}

let displayNames: Intl.DisplayNames | null | undefined;
/** "German" for `de` (the code when the runtime cannot name it). */
export function languageName(code: string): string {
  if (displayNames === undefined) {
    try {
      displayNames = new Intl.DisplayNames(['en'], { type: 'language' });
    } catch {
      displayNames = null;
    }
  }
  try {
    return displayNames?.of(code) ?? code;
  } catch {
    return code;
  }
}

const languageButtonSx = {
  textTransform: 'none',
  color: 'text.primary',
  backgroundColor: GUEST.activeNav,
  px: 1.5,
  py: 0,
  borderRadius: 1,
  border: '1px solid lightgray',
} as const;

function LanguageLabel({ code }: { code: string }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center' }}>
      <span role="img" aria-label={`${languageName(code)} flag`} style={{ marginRight: '8px', fontSize: '1.4em' }}>
        {flagEmoji(code)}
      </span>
      <Typography color="primary">{code.toUpperCase()}</Typography>
    </Box>
  );
}

/** A waiver's language menu (`LanguageSelector.js` with the waiver's languages). */
export function GuestLanguageSelector({ languages, value, onChange, label }: { languages: readonly string[]; value: string; onChange: (code: string) => void; label: string }) {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  return (
    <div>
      <Button
        variant="outlined"
        aria-haspopup="true"
        aria-expanded={anchor ? 'true' : undefined}
        aria-label={`${label}: ${languageName(value)}`}
        onClick={(e) => setAnchor(e.currentTarget)}
        sx={languageButtonSx}
        endIcon={<KeyboardArrowDownIcon />}
      >
        <LanguageLabel code={value} />
      </Button>
      <Menu
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        {languages.map((code) => (
          <MenuItem
            key={code}
            selected={code === value}
            onClick={() => {
              onChange(code);
              setAnchor(null);
            }}
          >
            <LanguageLabel code={code} />
          </MenuItem>
        ))}
      </Menu>
    </div>
  );
}

// ---------------------------------------------------------------------------
// App bar
// ---------------------------------------------------------------------------

export function GuestAppBar({ sample, wide, progress }: { sample: GuestPreviewSample; wide: boolean; progress: number | null }) {
  return (
    <Box
      data-guest-appbar
      sx={{ position: 'relative', height: GUEST.appBarPx, bgcolor: 'background.paper', color: 'text.primary', borderBottom: 1, borderColor: 'divider', display: 'flex', alignItems: 'center', justifyContent: 'center', px: wide ? 3 : 2 }}
    >
      <Box sx={{ maxWidth: GUEST.maxWidthPx, width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: wide ? 0 : 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          {/* The operator's name shows from `lg` only (TourOperatorBrand.js). */}
          {wide && (
            <Typography variant="h6" sx={{ color: 'primary.main' }}>
              {sample.operatorName}
            </Typography>
          )}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }} aria-hidden>
          <Box sx={{ ...languageButtonSx, display: 'inline-flex', alignItems: 'center', gap: 0.5, minHeight: 36, border: '1px solid lightgray', borderRadius: `${GUEST.radius}px` }}>
            <LanguageLabel code="en" />
            <KeyboardArrowDownIcon fontSize="small" />
          </Box>
          <Typography component="span" sx={{ color: 'primary.main', fontWeight: 600, fontSize: '0.875rem', px: 1 }}>
            SIGN OUT
          </Typography>
        </Box>
      </Box>
      {progress !== null && (
        <LinearProgress variant="determinate" value={progress} sx={{ height: 4, position: 'absolute', bottom: 0, left: 0, width: '100%' }} />
      )}
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Sidebar
// ---------------------------------------------------------------------------

export type StepStatus = 'completed' | 'current' | 'pending';

function StepIcon({ status, lead }: { status: StepStatus; lead: boolean }) {
  if (status === 'completed') return <CheckCircleIcon sx={{ color: lead ? GUEST.successLight : 'success.main' }} fontSize="small" />;
  if (status === 'current') return <FiberManualRecordIcon sx={{ color: 'primary.main' }} fontSize="small" />;
  return <RadioButtonUncheckedOutlinedIcon sx={{ color: 'text.disabled' }} fontSize="small" />;
}

const navItemSx = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-start',
  width: '100%',
  px: 1.5,
  py: 0.75,
  borderRadius: 1,
  mb: 0.5,
  textAlign: 'left',
} as const;

export interface SidebarStep {
  def: PreviewStepDef;
  /** The guest app's label ("Lead Booker", "Guest 1", "Pre-check-in"). */
  label: string;
  status: StepStatus;
  active: boolean;
  lead: boolean;
}

function StepLinks({ steps, reachable, onStep }: { steps: readonly SidebarStep[]; reachable: boolean; onStep: (s: PreviewStepDef) => void }) {
  return (
    <Box data-guest-steps>
      {steps.map((s) => {
        const body: ReactNode = (
          <>
            <Box sx={{ minWidth: 32, display: 'flex' }}>
              <StepIcon status={s.status} lead={s.lead} />
            </Box>
            <Typography sx={{ fontWeight: reachable ? 600 : 400, fontSize: '1rem' }}>{s.label}</Typography>
          </>
        );
        return reachable ? (
          <ButtonBase
            key={s.def.id}
            data-guest-step-link={s.def.id}
            aria-current={s.active ? 'step' : undefined}
            onClick={() => onStep(s.def)}
            sx={{ ...navItemSx, color: 'text.primary', bgcolor: s.active ? GUEST.activeNav : 'transparent', '&:hover': { bgcolor: s.active ? GUEST.activeNav : 'action.hover' } }}
          >
            {body}
          </ButtonBase>
        ) : (
          <Box key={s.def.id} data-guest-step-link={s.def.id} sx={{ ...navItemSx, color: 'text.disabled' }}>
            {body}
          </Box>
        );
      })}
    </Box>
  );
}

function TripCard({ sample }: { sample: GuestPreviewSample }) {
  return (
    <Box sx={{ p: 2, textAlign: 'left' }}>
      <Typography variant="caption" display="block" gutterBottom sx={{ fontSize: '14px', color: 'text.secondary' }}>
        {sample.startLabel}
      </Typography>
      <Typography variant="h6" component="div" sx={{ fontWeight: 600, fontSize: '16px' }}>
        {sample.tourName}
      </Typography>
      <Typography variant="body1" component="div" color="text.secondary" sx={{ mt: 1 }}>
        Booking:&nbsp;
        <Chip
          label={sample.bookingRef.toUpperCase()}
          size="small"
          sx={{ bgcolor: 'grey.200', color: 'text.secondary', mr: 0.8, mb: 0.8, borderRadius: 0, fontWeight: 600, fontSize: '12px', lineHeight: '20px' }}
        />
      </Typography>
    </Box>
  );
}

export interface GuestSidebarProps {
  sample: GuestPreviewSample;
  intro: string;
  steps: readonly SidebarStep[];
  /** "Step N of M" (null on the success screen). */
  position: { n: number; of: number } | null;
  reachable: boolean;
  onStep: (s: PreviewStepDef) => void;
  wide: boolean;
}

export function GuestSidebar({ sample, intro, steps, position, reachable, onStep, wide }: GuestSidebarProps) {
  const [open, setOpen] = useState(false);
  if (!wide) {
    return (
      <Box data-guest-sidebar="phone" sx={{ borderRadius: 1, boxShadow: 2, bgcolor: 'background.paper', mb: 2 }}>
        <TripCard sample={sample} />
        <Divider />
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, p: 2 }}>
          <Box sx={{ textAlign: 'left' }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              pre-check-in process
            </Typography>
            <Typography component="span" color="text.secondary">
              {intro}
            </Typography>
          </Box>
          <IconButton
            size="small"
            aria-label={open ? 'Hide the steps' : 'Show the steps'}
            aria-expanded={open}
            onClick={() => setOpen((o) => !o)}
            sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 1, bgcolor: 'background.paper', color: 'primary.main' }}
          >
            {open ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
          </IconButton>
        </Box>
        {open && (
          <Box sx={{ p: 2, pt: 1 }}>
            <StepLinks steps={steps} reachable={reachable} onStep={onStep} />
          </Box>
        )}
      </Box>
    );
  }
  return (
    <Box
      data-guest-sidebar="desktop"
      sx={{ width: GUEST.sidebarPx, flex: 'none', boxShadow: 2, bgcolor: 'background.paper', borderRadius: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column', minHeight: 400 }}
    >
      <TripCard sample={sample} />
      <Divider />
      <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
        <Box sx={{ p: 2, pb: 1, textAlign: 'left' }}>
          <Typography variant="h6" gutterBottom sx={{ fontSize: '16px' }}>
            Pre-check-in process
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {intro}
          </Typography>
        </Box>
        <Box sx={{ flexGrow: 1, px: 2, pb: 1 }}>
          <StepLinks steps={steps} reachable={reachable} onStep={onStep} />
        </Box>
      </Box>
      {position && (
        <>
          <Divider sx={{ my: 2 }} />
          <Box sx={{ px: 2, pb: 2, textAlign: 'left' }}>
            <Typography variant="body1" gutterBottom>
              Step {position.n} of {position.of}
            </Typography>
            <LinearProgress variant="determinate" value={(position.n / position.of) * 100} sx={{ height: 6, borderRadius: 2, mt: 1 }} />
          </Box>
        </>
      )}
    </Box>
  );
}
